import { Suspense } from 'react';

import { useSplitTestBranch } from '@api/lendingAPI/lapiHooks';
import { IconCheckDefault, IconShieldCheck } from '@iwoca/icons';
import { Spinner } from '@iwoca/orion';
import { Text } from '@iwoca/orion-ui';
import { FormattedMessage, useIntl } from 'react-intl';

import styles from './trueLayer.module.css';
import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';

type TBranch = 'INCLUDE' | 'EXCLUDE';

export const OpenBankingInfo = ({
    suggestedAmount
}: {
    suggestedAmount?: number;
}) => {
    return (
        <ErrorBoundary fallback={() => <ExcludeBranchCopy />}>
            <Suspense
                fallback={<Spinner dataTestId="open-banking-info-loading" />}
            >
                <OpenBankingCopy suggestedAmount={suggestedAmount} />
            </Suspense>
        </ErrorBoundary>
    );
};

const OpenBankingCopy = ({ suggestedAmount }: { suggestedAmount?: number }) => {
    const branch = useSplitTestBranch({
        // This test is only running in DE, so UK customers will get a not found error and fallback to the exclude branch copy in the error boundary
        test: 'de_open_banking_copy_test_2025_q3'
    }) as TBranch;

    if (branch === 'INCLUDE')
        return <IncludeBranchCopy suggestedAmount={suggestedAmount} />;

    return <ExcludeBranchCopy />;
};

const IncludeBranchCopy = ({
    suggestedAmount
}: {
    suggestedAmount?: number;
}) => {
    const isAutoAmount = suggestedAmount ? suggestedAmount <= 15000 : false;

    return (
        <div className="space-y-xl">
            <div className="flex gap-xl">
                <Text className="font-bold text-structure-40">
                    <FormattedMessage
                        id="_.true_layer.link_bank_account"
                        defaultMessage="Link my bank account"
                    />
                </Text>
                <div className="flex items-center gap-s">
                    <IconShieldCheck />
                    <Text className="text-xs font-bold">
                        <FormattedMessage
                            id="_.bank_selection_modal.bank_grade_encryption"
                            defaultMessage="Bank-grade encryption"
                        />
                    </Text>
                </div>
            </div>
            <div className="space-y-s">
                <IconWithText
                    message={
                        isAutoAmount ? (
                            <FormattedMessage
                                id="_.open_banking.info.fast_auto_amount"
                                defaultMessage="Fast - we provide decisions 2x faster than if you upload your bank statements manually."
                            />
                        ) : (
                            <FormattedMessage
                                id="_.open_banking.info.fast"
                                defaultMessage="Fast - we provide decisions faster than if you upload your bank statements manually."
                            />
                        )
                    }
                />
                <IconWithText
                    message={
                        <FormattedMessage
                            id="_.open_banking.info.popular"
                            defaultMessage="Popular - most of our customers choose to link their bank account"
                        />
                    }
                />
                <IconWithText
                    message={
                        <FormattedMessage
                            id="_.open_banking.info.secure"
                            defaultMessage="Secure - your password is never shared. {learnMoreLink}."
                            values={{ learnMoreLink: <LearnMoreLink /> }}
                        />
                    }
                />
                <IconWithText
                    message={
                        <FormattedMessage
                            id="_.open_banking.info.easy"
                            defaultMessage="Easy - it just takes a few clicks."
                        />
                    }
                />
            </div>

            <Text>
                <FormattedMessage
                    id="_.open_banking.info.link_accounts_with_revenue"
                    defaultMessage="Link all accounts that represent more than 10% of your total revenue."
                />
            </Text>
        </div>
    );
};

const IconWithText = ({ message }: { message: React.ReactNode }) => {
    return (
        <div className="flex gap-s">
            <IconCheckDefault className="size-xl shrink-0 fill-success-mid" />
            <Text className="font-med">{message}</Text>
        </div>
    );
};

const ExcludeBranchCopy = () => {
    return (
        <div
            className="space-y-m"
            data-testid="open-banking-info-exclude-branch"
        >
            <Text className="font-bold text-structure-40">
                <FormattedMessage
                    id="_.true_layer.link_bank_account"
                    defaultMessage="Link my bank account"
                />
            </Text>
            <ul className="list-inside list-disc">
                <li className="font-reg text-structure-40">
                    <FormattedMessage
                        id="_.true_layer.info"
                        defaultMessage="Open Banking is a secure way to share your business banking data without sharing your password. When you link your account with Open Banking, it means we can assess your application more quickly, now and in the future. {link}"
                        values={{
                            link: <LearnMoreLink />
                        }}
                    />
                </li>
                <li className="font-reg text-structure-40">
                    <FormattedMessage
                        id="_.true_layer.more_info"
                        defaultMessage="Your transactions must cover the stated period and be from the account(s) where the majority of your business revenue is generated."
                    />
                </li>
            </ul>
        </div>
    );
};

const LearnMoreLink = () => {
    const intl = useIntl();

    return (
        <a
            className={styles.link}
            href={intl.formatMessage({
                id: '_.true_layer.info_link',
                defaultMessage:
                    'https://www.openbanking.org.uk/what-is-open-banking/'
            })}
            target="_blank"
            rel="noopener noreferrer"
        >
            <FormattedMessage
                id="_.true_layer.info_link_text"
                defaultMessage="Learn More"
            />
        </a>
    );
};
